import { get } from 'lodash';
import { autorun } from 'mobx';
import React, { useCallback, useEffect } from 'react';
import { FB, FBEditorValidationProps, FBSchemaProps, FBSchemaState } from '..';
import predefinedValidators from '../data/predefinedValidators.json';

const validatorFields = predefinedValidators
  .filter(item => item.validationMode === 'visibility')
  .map(({ name }) => ({
    name,
    validatorName: name.slice(0, -'.name'.length),
    valueName: name.replace(/\.name$/, '.value'),
  }));

export const withFBEditorValidation = <T extends FBEditorValidationProps>(
  Component: React.FunctionComponent<T>,
) => {
  const Comp = ({
    schemaState,
    schema,
    ...props
  }: T) => {
    const { formState } = FB.useStores();

    const configureField = useCallback((item: FBSchemaProps): FBSchemaProps => {
      const field = validatorFields.find(field => item.name === field.name || item.name === field.valueName);
      if (!field) { return item; }
      // Enabled rules start as true and become { name, value } when configured.
      const enabled = Boolean(get(formState?.values, field.validatorName));
      const isSelector = item.name === field.name;
      const rules = enabled ? 'required' : '';
      if (item.hidden === !enabled && item.validationMode === 'default' && (!isSelector || item.rules === rules)) {
        return item;
      }
      return { ...item, hidden: !enabled, validationMode: 'default', ...(isSelector && { rules }) };
    }, [formState]);

    schema = (predefinedValidators as FBSchemaProps[]).map(configureField);
    schemaState = FB.useRef<FBSchemaState>(FBSchemaState, schema);

    useEffect(() => autorun(() => {
      const currentSchema = schemaState?.schema ?? [];
      const nextSchema = currentSchema.map(configureField)
        .filter(item => !item.hidden || !validatorFields.some(field => item.name === field.valueName));
      if (nextSchema.length !== currentSchema.length || nextSchema.some((item, index) => item !== currentSchema[index])) {
        schemaState?.setSchema(nextSchema);
      }
      // Hidden controls may never mount, so the editor owns their values and rules.
      validatorFields.forEach(({ name, validatorName, valueName }) => {
        const enabled = Boolean(get(formState?.values, validatorName));
        const rules = enabled ? 'required' : '';
        if (!enabled) {
          formState?.omitFieldValue(name);
          formState?.omitFieldValue(valueName);
        }
        if (formState && formState.rules[name] !== rules) {
          formState.setFieldRules(name, rules);
          formState.setFieldFinalValidationRules(name, rules);
        }
      });
    }), [configureField, formState, schemaState]);

    return Component({
      ...(props as T),
      initialValues: formState?.initialValues,
      schemaState,
      schema,
    });
  };

  return (props: T) => Comp(props);
};
